<template>
  <v-page-content>
    <b-card>
      <div class="logs-table p-2" ref="gridDiv">
        <ag-grid-vue
          style="height: 100%;"
          class="ag-theme-material custom-table"
          rowHeight="78"
          :gridOptions="gridOptions"
          :columnDefs="columnDefs"
          :rowData="logs"
          :pagination="true"
          :suppressPaginationPanel="true"
          :paginationPageSize="paginationPageSize"
          :currentPage="currentPage"
          @filterChanged="onFilterChanged"
          @grid-ready="onGridReady"
          @gridSizeChanged="onGridSizeChanged"
          :frameworkComponents="frameworkComponents"
        />
      </div>
      <div class="mt-2 text-right">
        <paginator
          :pages="pages"
          :currentPage="currentPage"
          :pageSize="paginationPageSize"
          :total="total"
          :itemsCount="logs.length"
          @toFirstPage="onToFirstPage"
          @toLastPage="onToLastPage"
          @previousPage="onPreviousPage"
          @nextPage="onNextPage"
        />
      </div>
    </b-card>
  </v-page-content>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import moment from 'moment'
import _ from 'lodash'
import AG_GRID_LOCALE_RU from '@/config/locale.ru'
import RoleRenderer from '@/components/helpers/renderers/RoleRenderer'
import PartialMatchFilter from '@/components/helpers/filters/PartialMatchFilter'
import logsApi from '@/api/logs'
import Paginator from '@/components/helpers/Paginator.vue'
import { actionsOnStudents } from '@/config/student-actions'

export default {
  name: 'StudentsHistory',
  components: {
    'ag-grid-vue': AgGridVue,
    Paginator
  },
  data() {
    return {
      pages: 1,
      currentPage: 1,
      total: 0,
      paginationPageSize: 20,
      logs: [],
      rowData: null,
      gridApi: null,
      gridOptions: {
        defaultColDef: {
          sortable: false,
          resizable: true,
          autoHeight: false,
          filter: 'partialMatchFilter',
          headerClass: 'ag-grid-table-header',
          cellStyle: {
            'word-break': 'break-all'
          }
        },
        localeText: AG_GRID_LOCALE_RU,
        headerHeight: 40,
        enableCellTextSelection: true,
        suppressMenuHide: true
      },
      frameworkComponents: {
        roleRenderer: RoleRenderer,
        partialMatchFilter: PartialMatchFilter
      },
      columnDefs: [
        {
          headerName: 'Сотрудник',
          field: 'user',
          width: 160
        },
        {
          headerName: 'Роль',
          field: 'roles',
          suppressSizeToFit: true,
          filter: false,
          cellRenderer: 'roleRenderer',
          width: 110
        },
        {
          headerName: 'Действие',
          field: 'type',
          suppressSizeToFit: true,
          filter: true,
          filterParams: {
            filterOptions: [
              {
                displayKey: 'empty',
                test: () => true,
                hideFilterInput: true
              },
              ...Object.entries(actionsOnStudents).map(([key, value]) => ({
                displayKey: key,
                displayName: value,
                test: () => true,
                hideFilterInput: true
              }))
            ],
            suppressAndOrCondition: true,
            newRowsAction: 'keep'
          },
          width: 150
        },
        {
          headerName: 'Студент',
          field: 'student',
          width: 160
        },
        {
          headerName: 'Организация',
          field: 'organization',
          width: 160
        },
        {
          headerName: 'Дата',
          field: 'createdAt',
          filter: 'agDateColumnFilter',
          filterParams: {
            suppressAndOrCondition: true,
            filterOptions: ['equals'],
            closeOnApply: true,
            newRowsAction: 'keep'
          },
          width: 120
        }
      ]
    }
  },
  created() {
    this.debouncedFetchFilteredData = _.debounce(() => {
      this.currentPage = 1
      this.fetchLogs()
    }, 500)
    this.debouncedFetchFilteredData()
  },
  methods: {
    async fetchLogs() {
      const response = await logsApi.getStudentsChangeLog(this.getFilters(), this.currentPage, this.paginationPageSize)
      this.pages = response.data.pages
      this.total = response.data.total
      this.logs = response.data.docs.map((log) => ({
        user: log.user.email,
        roles: log.user.roles,
        student: log.student.email,
        organization: log.student.organization.title,
        type: actionsOnStudents[log.type],
        createdAt: moment(log.createdAt).format('DD.MM.YYYY')
      }))
    },
    onGridReady(params) {
      this.gridApi = params.api
      this.onGridSizeChanged()
    },
    onGridSizeChanged() {
      if (this.$refs.gridDiv.clientWidth >= 700) {
        this.gridApi.sizeColumnsToFit()
      }
    },
    onFilterChanged() {
      this.debouncedFetchFilteredData()
    },
    getFilters() {
      if (this.gridApi) {
        const filterModel = _.cloneDeep(this.gridApi.getFilterModel())
        return Object.entries(filterModel)
          .map(([key, value]) => ({
            param: key,
            needle: _.hasIn(value, 'value') ? value.value : (value.dateFrom || value.type),
            isStrict: false
          }))
      }
      return []
    },
    onPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1
        this.fetchLogs()
      }
    },
    onNextPage() {
      if (this.currentPage < this.pages) {
        this.currentPage += 1
        this.fetchLogs()
      }
    },
    onToFirstPage() {
      this.currentPage = 1
      this.fetchLogs()
    },
    onToLastPage() {
      if (this.currentPage < this.pages) {
        this.currentPage = this.pages
        this.fetchLogs()
      }
    }
  }
}
</script>

<style lang="scss">
.logs-table {
  height: calc(100vh - 220px);
  border-bottom: 1px solid lightgray;
}

</style>
